import React from 'react'

import { formatNumber } from 'utils'

import { InfoIcon } from 'lucide-react'

import { useTheme, Box, Typography, Stack, Tooltip } from '@mui/material'

import { Card } from '@repo/ui'

import { InfoTooltip, caveatMarkSx } from 'components/feedback/InfoTooltip'

/**
 * What a tile renders in the figure slot when it has no figure.
 *
 * Exported so the callers that produce it (`card.value ?? EM_DASH_PLACEHOLDER`) and the
 * check below that consumes it read the same character. The empty-value branch turns on
 * this string, and a caller writing its own em-dash — there are two visually identical
 * ones in Unicode, and `-` is a third thing entirely — would silently miss it.
 */
export const EM_DASH_PLACEHOLDER = '—'


type AnalyticsBlockProps = {
  title: string
  icon?: React.ReactNode
  iconPosition? : 'left' | 'right'
  /**
   * The figure. A NUMBER is run through `formatNumber` (thousands separators); a
   * STRING is rendered verbatim, for values this component cannot format itself —
   * currency, percentages, and the em-dash placeholder a blocked metric shows
   * instead of inventing one. Widened rather than forked, per §47: the ROI cards
   * needed the same tile and the alternative was a second near-identical card.
   *
   * An ELEMENT stands in the figure's slot for a metric that has no figure AND can
   * get one — VRG-459's `N inputs needed` button. Widened here rather than by the
   * caller drawing its own tile, for the same reason as the string: a second tile
   * would be a copy of this file's title row, stat typography and note styling that
   * drifts on the first change applied to one and not the other.
   *
   * ⚠️ NOT `React.ReactNode`. That would admit `null` and `undefined`, and a tile
   * whose figure slot renders nothing at all is the one state this component must
   * not have — the em-dash placeholder exists precisely so an absent figure is
   * VISIBLY absent rather than blank.
   */
  stat: number | string | React.ReactElement
  /**
   * OPTIONAL, and always was in practice: the render guards on `{prevStat && …}` and no
   * call site has ever passed it, so every `<AnalyticsBlock>` in the app carried a
   * `TS2741: Property 'prevStat' is missing` — 3 of the platform's 1076 baseline errors
   * were this one prop. Declaring it required made the type disagree with both the
   * component's own body and every consumer, and it charged one new error per card added
   * (the 4th Inventory card would have made it 4). Fixed at the declaration.
   */
  prevStat?: number
  interval?: string
  description?: string
  /**
   * Basis line under the figure — "total scans", "countries", "2 of 2 scanned items".
   * Says WHAT was counted, so a bare number cannot be misread as something else.
   */
  unit?: string
  /**
   * A caveat on the figure — for a number that is arithmetically right but silently
   * incomplete (Geographic Reach reading 0 while the map plots a pin), or the reason
   * there is no number at all.
   *
   * ⚠️ IT NO LONGER RENDERS AS AN AMBER LINE ON THE CARD FACE. It goes into the tile's
   * `(i)` tooltip, and the `(i)` is marked (`caveatMarkSx`) while one is live.
   *
   * This REVERSES the "stated inline, never in a tooltip" rule this prop was built
   * around, and the reason is that the rule assumed something about these caveats that
   * turned out to be false: it assumed they were STATES, which clear. Most are
   * PROPERTIES OF THE DATA and never clear — Geographic Reach's unresolved scans stay
   * unresolved; the transfers that completed before Vera recorded completion times will
   * always have completed before it. On an all-time view the amber line is permanent, and
   * a permanent amber line is wallpaper. It stops being read, and it takes the caveat
   * that DID matter down with it, since they are the same colour on the same page.
   *
   * 🚨 ONE EXCEPTION, and it is not negotiable: when the caveat is the reason the figure
   * is ABSENT, hiding it behind hover leaves a card that reads as broken — an em-dash with
   * no account of itself. So a caveat on an empty value stays on the face, in
   * `text.secondary` rather than amber, because it is an explanation and not a warning.
   * Detected from the em-dash, not from a list of metric ids: see `showCaveatInline`.
   */
  note?: string
  /** `true` shows a bare info icon; a string or node additionally shows it as a hover tooltip. */
  tooltip?: boolean | React.ReactNode
  /**
   * A glossary entry id. Prefer this over `tooltip` whenever the card's title is a term of
   * art — a stat tile is exactly where §59 says a definition belongs, and reading it from
   * the glossary is what stops the tile and the glossary page drifting apart (§44).
   *
   * `term` and `tooltip` compose, and the combination is the useful one here rather than a
   * fallback: `tooltip` becomes the body while the footer still deep-links to the full
   * definition. Some cards genuinely need local phrasing — the Inventory Capacity CARD is
   * floored at zero per tag type while the Capacity COLUMN is signed, so the card must not
   * be described by a sentence that promises negatives — and this lets a card say what it
   * shows without cutting the reader off from the term behind it.
   */
  term?: string
  /**
   * Where the tile draws its own surface.
   *
   * `'card'` (default) is every existing call site, unchanged: the tile IS a card.
   * `'bare'` renders the same contents with NO `Card` around them, for a caller that has
   * already drawn one and is placing several tiles inside it — today the ROI hero, which is
   * a single full-width card holding three figures. Three `<Card>`s nested inside a fourth
   * is the card-in-a-card defect this page already hit once with `ScansByTime2` and the
   * panel shell, and it is what forced the hero to hand-roll its own typography instead.
   *
   * Deliberately a variant of THIS component rather than a second one (§36/§47). The hero's
   * three figures are the same object as the KPI tiles beside them — a label, a figure, an
   * optional caveat, an optional info tooltip — so a parallel component would have been a
   * copy of this file's title row, stat typography and note styling, drifting from it on the
   * first change applied to one and not the other.
   */
  surface?: 'card' | 'bare'
  /**
   * Figure size in px. 32 is the KPI-tile size and the default.
   *
   * Raised only where the tile IS the page headline: the ROI hero passes 44, so it reads as
   * at least the equal of the tiles below it while still coming from this one component.
   */
  statSize?: number
  sx?: object
}

export const AnalyticsBlock = ({ title, icon, iconPosition = 'left', stat, prevStat, interval, description, unit, note, tooltip = false, term, surface = 'card', statSize = 32, sx }: AnalyticsBlockProps) => {
  const theme = useTheme()

  // Only meaningful for a numeric stat. A string value is already formatted and has
  // no comparable magnitude, so the delta block simply does not render for one.
  const numericStat = typeof stat === 'number' ? stat : null

  /**
   * The percentage change, or `null` when there ISN'T one.
   *
   * The `null` case is load-bearing and used to be silently wrong. A prior period of
   * ZERO has no percentage growth — the ratio is undefined, not large — but the old
   * expression was `prevStat && … ? stat / prevStat : 0`, so `prevStat === 0` fell
   * through the falsy guard to `0` and the card rendered **"-100.00%" in error red for
   * a metric that had just grown from nothing**. That is the exact inversion of the
   * truth, in the one case a brand most wants to see (first scans, first transfers),
   * and it would have shipped the moment any call site passed `prevStat` — which is
   * why it is fixed here rather than at the consumers.
   *
   * `0 → 0` is likewise not "0% change" worth drawing attention to; it is two periods
   * of nothing, so it also returns null and the block does not render.
   */
  const pctChange =
    prevStat != null && numericStat != null && prevStat !== 0
      ? (numericStat / prevStat) * 100 - 100
      : null
  const greaterThanPrevious = pctChange != null && pctChange > 0

  /**
   * Is the figure slot EMPTY — an em-dash or nothing?
   *
   * Generic on purpose. The obvious version of the exception in `note`'s docs is "keep
   * Transfer completion time's caveat inline", and that version is wrong twice over: it
   * would not fire for the blocked and `not_computed` cards, which are the same shape and
   * far more numerous, and it would keep firing for Transfer completion time on the day it
   * finally has a figure. The property that matters is "there is no number here", which is
   * exactly what the em-dash already means.
   *
   * An ELEMENT stat is never empty for this purpose: it is the `N inputs needed` button,
   * which is itself the account of the missing figure.
   */
  const statIsEmpty =
    typeof stat === 'string' && (stat.trim() === '' || stat.trim() === EM_DASH_PLACEHOLDER)
  /** A caveat explaining an absent figure stays on the face; every other caveat moves. */
  const showCaveatInline = Boolean(note) && statIsEmpty

  const body = (
      <Stack justifyContent="space-between" spacing={3}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center" display="flex">
              {/* Gated on `icon`, not on `iconPosition` alone. The box is a 32px bordered,
                  filled square, so a card with no icon rendered an EMPTY tile — which is
                  worse than the icon it replaced. `icon` is optional in the props, so this
                  was reachable before anyone passed no icon deliberately. Cards that do
                  pass one are unaffected. */}
              {icon && iconPosition == 'left' && (
                <Box sx={{ 
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 32,
                  minWidth: 32,
                  borderRadius: 2, 
                  border: `1px solid ${theme.palette.primary[200]}`,
                  backgroundColor: theme.palette.primary[50]
                }}>   
                  {icon}
                </Box>
              )}
              {/*
                `body3` (14px), not `body2` (16px). A stat's LABEL is chrome — it names
                the figure, it is not the figure — and 16px made it the largest text in
                the analytics control band, competing with the number it introduces.

                14px is the size the rest of that band already uses: the map's
                `Group by:` and the ROI chart's `Display:` are both `body3`. This makes
                the stat label the third member of that set rather than an outlier.

                Applied to EVERY AnalyticsBlock, not just the ROI hero, deliberately.
                Scoping it to the hero (which is distinguishable — it passes
                `statSize={56}` where the KPI tiles pass 32) would leave the page's
                biggest figure carrying its SMALLEST label, which is backwards. The
                label is the same kind of thing in both places, so it takes one size.
              */}
              <Typography variant="body3" display="block">
                {title}
              </Typography>
              {/*
                `term` wins when both are given: `InfoTooltip` takes `tooltip` as its body
                and still footers through to the definition, so nothing is lost by
                preferring it. The second branch stays for the cards that explain a CONTROL
                or a figure with no term behind it, which is a legitimate use of literal
                text and not a migration backlog.

                A caveat rides in `caveat` on BOTH branches rather than being appended to
                the body, so a card that later gains a `term` keeps its definition AND its
                caveat instead of one silently replacing the other. Suppressed when the
                caveat is already on the face (`showCaveatInline`) — the same sentence twice
                on one card reads as a rendering fault, not as emphasis.

                ⚠️ The literal-text branch now goes through `InfoTooltip` too, but KEEPS ITS
                OWN GLYPH: `InfoIcon` at 16 rather than the house glyph at 14.

                The SHAPE half of that split is gone — `InfoGlyph` was `CircleHelpIcon` and is
                now `InfoIcon` (VRG-517), so the two branches finally draw the same mark. What
                remains is the SIZE, 16 here against 14 there, which is what the stat tiles were
                built to: a marked card's glyph has to match the unmarked cards in the same row.
                Converging the two sizes is still a sweep of its own.

                ⚠️ The reason originally recorded here — "none of the 22 ROI cards carries a
                `term`, so switching the glyph would have changed the icon on every card in
                the row" — NO LONGER HOLDS. As of VERA-260 all 22 do, along with the four KPI
                tiles and the three ROI-hero figures, so the analytics surface is now entirely
                on the `term` branch and the glyph difference is invisible there. What keeps
                the split alive is the OTHER call sites: the tiles that explain a CONTROL
                rather than a term of art, which `term`'s own doc above says are a legitimate
                use of literal text and not a migration backlog. So this is now a decision
                about those, not about the ROI row — and anyone converging the glyphs should
                check the surfaces that still take `tooltip` alone, not this file's history.
              */}
              {term ? (
                <InfoTooltip
                  term={term}
                  text={typeof tooltip === 'boolean' ? undefined : tooltip}
                  caveat={showCaveatInline ? undefined : note}
                />
              ) : (tooltip || note) &&
                (typeof tooltip === 'boolean' && (showCaveatInline || !note) ? (
                  // Nothing to say on hover: `tooltip` is the bare `true` and any caveat is
                  // already on the face. The glyph alone, exactly as before.
                  <InfoIcon strokeWidth={1} size={16} color={theme.palette.grey[600]} />
                ) : (
                  <InfoTooltip
                    text={typeof tooltip === 'boolean' ? undefined : tooltip}
                    caveat={showCaveatInline ? undefined : note}
                  >
                    <Box
                      component="span"
                      tabIndex={0}
                      // Keyed on what the TOOLTIP actually carries, not on whether a note
                      // exists. When the caveat is already on the card face, announcing
                      // "including a caveat" here promises a screen-reader user a second
                      // thing to open that is not in this tooltip.
                      aria-label={
                        showCaveatInline || !note
                          ? 'More information'
                          : 'About this figure, including a caveat'
                      }
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'help',
                        // Marked only while a caveat is actually live AND hidden. A card
                        // whose caveat is on its face has already said it.
                        ...(showCaveatInline || !note ? null : caveatMarkSx(theme)),
                      }}
                    >
                      {/* No `color` when marked — the disc owns the tone and the glyph
                          inherits it, so the two cannot disagree. */}
                      <InfoIcon
                        strokeWidth={1}
                        size={16}
                        color={showCaveatInline || !note ? theme.palette.grey[600] : undefined}
                      />
                    </Box>
                  </InfoTooltip>
                ))}
            </Stack>
            {icon && iconPosition == 'right' && (
                <Box sx={{ 
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 32,
                  minWidth: 32,
                  borderRadius: 2, 
                  border: `1px solid ${theme.palette.primary[200]}`,
                  backgroundColor: theme.palette.primary[50]
                }}>   
                  {icon}
                </Box>
              )}
        </Stack>

        {/*
          An ELEMENT stat is rendered in its own box rather than inside the figure
          Typography. A control inheriting a 32px `fontSize` and weight 600 would be
          a button drawn at headline scale, which is the opposite of what the
          stand-in should read as — it stands where the figure would be, and says
          the figure is obtainable, not that it IS the figure. The box keeps the
          slot's block layout so the basis line and caveat below it do not move.
        */}
        {typeof stat === 'string' || typeof stat === 'number' ? (
          /* VRG-512: `statIsEmpty` used to render the em-dash at the SAME weight and size as
           * a real figure — on the ROI hero that is a 56px, weight-600 dash, which reads as a
           * rendering glitch rather than an intentional "nothing to show" state (a bare "—"
           * at headline scale looks broken, not quiet). The figure slot's TYPOGRAPHY is now
           * part of what "empty" means, not just its content: lighter weight, a muted token
           * (`text.disabled`, not the title color) and a scaled-down size, so the eye reads it
           * as absence rather than as a number that failed to load.
           *
           * Scaled at 0.75×, not a fixed px — this component serves both the 32px KPI-tile
           * default and the 56px ROI-hero multiple (`HERO_MULTIPLE_STAT_SIZE`), and a flat
           * override would either be illegibly small on the tiles or barely reduced on the
           * hero. A ratio keeps the same relative de-emphasis at either size.
           *
           * The explanatory sentence this pairs with (`NO_SPEND_EXPLANATION` on the ROI hero;
           * `note` + `showCaveatInline` below on every other tile) is untouched — muting the
           * dash is what gives that sentence more of the reader's attention, without needing
           * to make the sentence itself louder (which would cut against the "quiet,
           * non-alarming" empty state this is explaining).
           */
          <Typography
            variant="h4"
            sx={{
              fontWeight: statIsEmpty ? 400 : 600,
              fontSize: statIsEmpty ? statSize * 0.75 : statSize,
              color: statIsEmpty ? theme.palette.text.disabled : undefined,
            }}
            display="block"
          >
            {typeof stat === 'number' ? formatNumber(stat) : stat}
          </Typography>
        ) : (
          // Matched to the figure's own line box so a tile showing a control is the
          // same height as the tile beside it showing a number.
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: statSize * 1.235 }}>
            {stat}
          </Box>
        )}

        {unit && (
          <Typography variant="body3" color="text.secondary" display="block">
            {unit}
          </Typography>
        )}

        {/*
          ONLY when the caveat explains an ABSENT figure — see `showCaveatInline`. Every
          other caveat is in the `(i)` above, and the reasoning for that move is on the
          `note` prop.

          `text.secondary`, NOT `warning.main`. The card is showing an em-dash and this
          sentence is the account of why; that is context, and painting it amber makes an
          explained empty state look like a fault. Amber was also the specific thing that
          made these unreadable — a colour every card wore stopped meaning anything.

          STILL CLAMPED TO ONE LINE, with the full sentence on hover and in the `(i)`. It
          used to wrap freely, which was fine while a tile sized to its content and broke
          the moment the grid fixed tiles at 3x1 (160px): a two-line caveat spilled past the
          card's bottom edge and overlapped the card below it. Variable-height content in a
          fixed-height cell overflows by construction, so the clamp belongs here rather than
          at each caller — and the tooltip is where the sentence is read in full.
        */}
        {showCaveatInline && (
          <Tooltip title={note} arrow placement="top">
            {/*
              NO `title` on this child. MUI's Tooltip warns — loudly, once per instance,
              16 times on this page — when its child also carries `title`, because the
              native tooltip and the MUI one then both fire. The Tooltip above already
              supplies the accessible description.
            */}
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{
                lineHeight: 1.5,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
              }}
            >
              {note}
            </Typography>
          </Tooltip>
        )}

        {/*
          Three states, not two. A percentage renders when one exists; growth from a
          prior period of ZERO renders as "New" — neutral, because it is real movement
          but has no magnitude to report — and two empty periods render nothing at all.
          Inventing a number for either of the last two is what the old arithmetic did.
        */}
        {pctChange != null && (
          <Stack direction="row" alignItems="flex-end" spacing={2}>
            <Typography
              variant="body3"
              sx={{pb: 2.5}}
              color={greaterThanPrevious ? 'success.main' : 'error.main'}>
                {greaterThanPrevious && '+'}{pctChange.toFixed(2)}%
            </Typography>
            {interval && (
              <Typography variant="body3" color="text.secondary" sx={{pb: 2.5}}>vs. previous {interval}</Typography>
            )}
          </Stack>
        )}

        {pctChange == null && prevStat === 0 && numericStat != null && numericStat > 0 && (
          <Stack direction="row" alignItems="flex-end" spacing={2}>
            <Typography variant="body3" color="success.main" sx={{pb: 2.5}}>New</Typography>
            {interval && (
              <Typography variant="body3" color="text.secondary" sx={{pb: 2.5}}>none in the previous {interval}</Typography>
            )}
          </Stack>
        )}

      </Stack>
  )

  // `p: 6` belongs to the CARD, not to the contents — a bare tile is padded by whatever card
  // its caller already drew, so keeping the padding here would double it.
  // `overflow: hidden` is a BACKSTOP, not the fix — the clamped caveat above is. A tile
  // now lives in a fixed-height grid cell (3x1), so any future content that outgrows it
  // would escape the card and overlap its neighbour exactly as the caveat did. Clipping
  // at the card boundary makes that failure visible-but-contained rather than a figure
  // printed on top of another card. `height: 100%` so the card fills the cell it is
  // given instead of leaving a gap the grid has already reserved.
  return surface === 'bare' ? body : (
    <Card sx={{ p: 6, height: '100%', overflow: 'hidden' }}>{body}</Card>
  )
}